import React, { useState } from 'react';
import styled from 'styled-components';
import { getColorScheme } from './theme/colorSchemes';
import ChatArea from './components/ChatArea';
import LanguageSelector from './components/LanguageSelector';
import { FaMapMarkerAlt, FaClock, FaGlobe, FaEnvelope, FaPhone } from 'react-icons/fa';
import CRSCalculator from './components/CRSCalculator';
import CRSResults from './components/CRSResults';
import Rating from './rating';
import ReviewsPanel from './components/ReviewsPanel';

const JOURNEY_STEPS = [
  { id: 'welcome', label: 'Welcome' },
  { id: 'initial', label: 'Language' },
  { id: 'gathering', label: 'Info' },
  { id: 'documents', label: 'Docs' },
  { id: 'analysis', label: 'Analysis' },
  { id: 'connect', label: 'Advisor' },
];

/* ─── Styled Components ─── */
const MobileContainer = styled.div`
  height: 100vh; height: 100dvh;
  display: flex; flex-direction: column;
  background: #fff; overflow: hidden;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  &, & *, & *::before, & *::after { box-sizing: border-box !important; }
`;

const Header = styled.div`
  display: flex; align-items: center; gap: 12px;
  padding: 12px 16px;
  background: #fff;
  border-bottom: 1px solid #edf0f4;
  flex-shrink: 0; position: relative; z-index: 10;
`;

const HeaderAvi = styled.img`
  width: 40px; height: 40px; border-radius: 12px;
  object-fit: cover; flex-shrink: 0; cursor: pointer;
  border: 2px solid ${p => getColorScheme(p.$scheme).accentBorder};
  box-shadow: 0 2px 8px ${p => getColorScheme(p.$scheme).accentShadow};
`;

const HeaderInfo = styled.div`
  flex: 1; min-width: 0; cursor: pointer;
`;

const HeaderName = styled.div`
  font-size: 14.5px; font-weight: 700; color: #0f172a;
  display: flex; align-items: center; gap: 6px;
`;

const OnlineDot = styled.span`
  width: 7px; height: 7px; border-radius: 50%; background: #22c55e;
  box-shadow: 0 0 0 2px rgba(34,197,94,0.2);
`;

const HeaderSub = styled.div`
  font-size: 11.5px; color: #64748b; margin-top: 1px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;

/* ─── Progress Pill ─── */
const ProgressPill = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 10px 16px;
  background: ${p => getColorScheme(p.$scheme).accentSoft};
  border-bottom: 1px solid ${p => getColorScheme(p.$scheme).accentBorder};
  cursor: pointer; transition: all 0.2s; flex-shrink: 0;
`;

const MiniRingWrap = styled.div`
  position: relative; width: 40px; height: 40px; flex-shrink: 0;
`;

const MiniRingText = styled.div`
  position: absolute; inset: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 800; color: #0f172a;
`;

const PillInfo = styled.div`
  flex: 1; min-width: 0;
`;

const PillPhase = styled.div`
  font-size: 12px; font-weight: 700; color: #0f172a; margin-bottom: 1px;
`;

const PillHint = styled.div`
  font-size: 10px; color: #64748b;
`;

const PillArrow = styled.div`
  color: #94a3b8; transition: transform 0.2s; flex-shrink: 0;
  transform: ${p => p.$open ? 'rotate(180deg)' : 'rotate(0)'};
`;

const ProgressDetail = styled.div`
  max-height: ${p => p.$open ? '200px' : '0'};
  overflow: hidden; background: #fafbfc;
  border-bottom: ${p => p.$open ? '1px solid #edf0f4' : 'none'};
  transition: max-height 0.3s ease;
`;

const DetailInner = styled.div`
  padding: 12px 16px; display: flex; gap: 3px;
`;

const MSeg = styled.div`
  flex: 1; height: 4px; border-radius: 2px;
  background: ${p => p.$done ? '#22c55e' : p.$active ? getColorScheme(p.$scheme).primary : '#e2e5eb'};
`;

const SegLabels = styled.div`
  display: flex; justify-content: space-between;
  padding: 4px 16px 12px; font-size: 9px; color: #94a3b8; font-weight: 500;
`;

/* ─── Expert Drawer ─── */
const DrawerOverlay = styled.div`
  position: absolute; inset: 0; background: rgba(0,0,0,0.25);
  z-index: 20; opacity: ${p => p.$open ? 1 : 0};
  pointer-events: ${p => p.$open ? 'auto' : 'none'};
  transition: opacity 0.25s;
`;

const Drawer = styled.div`
  position: absolute; bottom: 0; left: 0; right: 0; z-index: 30;
  background: #fff; border-radius: 20px 20px 0 0;
  box-shadow: 0 -4px 24px rgba(0,0,0,0.08);
  transform: ${p => p.$open ? 'translateY(0)' : 'translateY(100%)'};
  transition: transform 0.3s cubic-bezier(0.4,0,0.2,1);
  max-height: 70%; overflow-y: auto;
  &::-webkit-scrollbar { display: none; }
`;

const DrawerHandle = styled.div`
  width: 36px; height: 4px; border-radius: 2px; background: #dde1e7;
  margin: 10px auto 4px;
`;

const DrawerCard = styled.div`
  padding: 16px 20px; text-align: center;
`;

const DrawerImg = styled.img`
  width: 56px; height: 56px; border-radius: 14px; object-fit: cover;
  border: 1.5px solid ${p => getColorScheme(p.$scheme).accentBorder};
  margin-bottom: 8px;
`;

const DrawerName = styled.div`
  font-size: 15px; font-weight: 700; color: #0f172a; margin-bottom: 2px;
`;

const DrawerTitle = styled.div`
  font-size: 11px; color: #64748b; margin-bottom: 8px;
`;

const DrawerItems = styled.div`
  padding: 0 20px 20px;
`;

const DrawerItem = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 10px 12px; border-radius: 10px;
  background: #fafbfc; border: 1px solid #edf0f4;
  margin-bottom: 6px;
`;

const DIIcon = styled.div`
  width: 30px; height: 30px; border-radius: 8px;
  background: ${p => getColorScheme(p.$scheme).accentSoft};
  color: ${p => getColorScheme(p.$scheme).primary};
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; flex-shrink: 0;
`;

const DILabel = styled.div`
  font-size: 9px; color: #94a3b8; font-weight: 500;
  text-transform: uppercase; letter-spacing: 0.3px;
`;

const DIValue = styled.div`
  font-size: 12px; color: #334155; font-weight: 500;
`;

const MobileSchedule = styled.div`
  max-height: ${p => p.$expanded ? '300px' : '0'};
  overflow: hidden; transition: max-height 0.3s ease, padding 0.3s ease;
  padding: ${p => p.$expanded ? '4px 0 2px' : '0'};
`;

const MobileScheduleRow = styled.div`
  display: flex; justify-content: space-between; padding: 3px 0;
  font-size: 11px; color: ${p => p.$isToday ? '#0f172a' : '#64748b'};
  font-weight: ${p => p.$isToday ? 600 : 400};
`;

/* ─── Component ─── */
const ChatPageMobile = ({
  darkMode, colorScheme, messages, expertData, sidebarOpen, toggleSidebar, toggleMode,
  listRef, handleMessagesContainerClick, loadingMessage,
  emojiPickerOn, data, emojiSelected,
  uploadRef, handleFileChange, uploadDoc, openEmojiPicker,
  inputRef, currentMessage, handleInputChange, sendMessage,
  apikey, base_url,
  isRecording, recordingTime, toggleRecording, isTranscribing, transcriptionStatus, formatTime,
  conversationStep, capturedInfo, uploadedDocuments, questionnaireProgress,
  widgetPhase, selectedLanguage, crsResult,
  onLanguageSelect, onChooseIntake,
  onCRSComplete, onCRSBack, onContinueToIntakeFromCRS, onDeclineIntake,
  // Which assessment questionnaires this adviser's discipline offers, and the single
  // handler that starts whichever one the visitor picks.
  assessmentTemplates = [], onChooseAssessment, oinpPhaseScreen = null,
  identificationComplete,
  otpProps,
}) => {
  const scheme = colorScheme || 'blue';
  const colors = getColorScheme(scheme);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [progressOpen, setProgressOpen] = useState(false);
  const [hoursExpanded, setHoursExpanded] = useState(false);
  const [reviewsOpen, setReviewsOpen] = useState(false);

  const currentStepIndex = JOURNEY_STEPS.findIndex(s => s.id === conversationStep);
  const qCurrent = questionnaireProgress?.current || currentStepIndex + 1;
  const qTotal = questionnaireProgress?.total || 25;
  const pct = Math.min(Math.round((qCurrent / qTotal) * 100), 100);
  const circumference = 2 * Math.PI * 16;
  const dashOffset = circumference - (circumference * pct / 100);
  const phaseName = JOURNEY_STEPS[currentStepIndex]?.label || 'Getting Started';

  const renderPhaseContent = () => {
    if (widgetPhase === 'language_select') {
      return <LanguageSelector onSelect={onLanguageSelect} scheme={scheme} />;
    }
    if (widgetPhase === 'crs') {
      return <CRSCalculator onComplete={onCRSComplete} onBack={onCRSBack} scheme={scheme} language={selectedLanguage} />;
    }
    if (widgetPhase === 'crs_results') {
      return <CRSResults result={crsResult} onContinueToIntake={onContinueToIntakeFromCRS}
        onDecline={onDeclineIntake} scheme={scheme} language={selectedLanguage} />;
    }
    // OINP screens are built by the parent so all three layouts share one
    // phase→screen mapping.
    if (oinpPhaseScreen) return oinpPhaseScreen;
    return null;
  };

  const phaseContent = renderPhaseContent();

  return (
    <MobileContainer>
      <Header>
        <HeaderAvi
          $scheme={scheme}
          src={expertData.imgUrl}
          alt={expertData.expertName}
          onClick={() => setDrawerOpen(true)}
        />
        <HeaderInfo onClick={() => setDrawerOpen(true)}>
          <HeaderName>
            {expertData.expertName} <OnlineDot />
          </HeaderName>
          <HeaderSub>{expertData.expertTilte}</HeaderSub>
        </HeaderInfo>
      </Header>

      {widgetPhase !== 'language_select' && (
        <>
          <ProgressPill $scheme={scheme} onClick={() => setProgressOpen(!progressOpen)}>
            <MiniRingWrap>
              <svg viewBox="0 0 36 36" style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
                <circle cx="18" cy="18" r="16" fill="none" stroke="#edf0f4" strokeWidth="4" />
                <circle cx="18" cy="18" r="16" fill="none" stroke={colors.primary} strokeWidth="4"
                  strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={dashOffset}
                  style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
              </svg>
              <MiniRingText>{qCurrent}<span style={{ fontSize: 8, fontWeight: 500, color: '#94a3b8' }}>/{qTotal}</span></MiniRingText>
            </MiniRingWrap>
            <PillInfo>
              <PillPhase>{phaseName}</PillPhase>
              <PillHint>Question {qCurrent} of ~{qTotal}</PillHint>
            </PillInfo>
            <PillArrow $open={progressOpen}>
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </PillArrow>
          </ProgressPill>

          <ProgressDetail $open={progressOpen}>
            <DetailInner>
              {JOURNEY_STEPS.map((step, i) => (
                <MSeg key={step.id} $scheme={scheme}
                  $done={i < currentStepIndex}
                  $active={i === currentStepIndex} />
              ))}
            </DetailInner>
            <SegLabels>
              {JOURNEY_STEPS.map((step, i) => (
                <span key={step.id} style={i === currentStepIndex ? { color: colors.primary, fontWeight: 700 } : {}}>
                  {step.label}
                </span>
              ))}
            </SegLabels>
          </ProgressDetail>
        </>
      )}

      {phaseContent ? (
        <div style={{ flex: 1, overflow: 'auto' }}>{phaseContent}</div>
      ) : (
        <ChatArea
          darkMode={false} scheme={scheme} messages={messages}
          listRef={listRef} handleMessagesContainerClick={handleMessagesContainerClick}
          loadingMessage={loadingMessage} emojiPickerOn={emojiPickerOn}
          emojiSelected={emojiSelected} openEmojiPicker={openEmojiPicker}
          uploadRef={uploadRef} handleFileChange={handleFileChange} uploadDoc={uploadDoc}
          inputRef={inputRef} currentMessage={currentMessage} handleInputChange={handleInputChange}
          sendMessage={sendMessage} apikey={apikey} base_url={base_url}
          isRecording={isRecording} recordingTime={recordingTime} toggleRecording={toggleRecording}
          isTranscribing={isTranscribing} transcriptionStatus={transcriptionStatus} formatTime={formatTime}
          questionnaireProgress={questionnaireProgress}
          widgetPhase={widgetPhase} selectedLanguage={selectedLanguage} identificationComplete={identificationComplete}
          assessmentTemplates={assessmentTemplates}
          handleChooseAssessment={onChooseAssessment} handleChooseIntake={onChooseIntake}
          showPoweredBy={true} showTimestamp={true}
          isMobile={true} isIframe={false}
          otpProps={otpProps}
        />
      )}

      <DrawerOverlay $open={drawerOpen} onClick={() => setDrawerOpen(false)} />
      <Drawer $open={drawerOpen}>
        <DrawerHandle />
        <DrawerCard>
          <DrawerImg $scheme={scheme} src={expertData.imgUrl} alt={expertData.expertName} />
          <DrawerName>{expertData.expertName}</DrawerName>
          <DrawerTitle>{expertData.expertTilte}</DrawerTitle>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
            <Rating
              rating={expertData.stars}
              reviews={expertData.reviewAmount}
              onClick={expertData.reviewAmount > 0 ? () => setReviewsOpen(true) : undefined}
            />
          </div>
        </DrawerCard>

        {reviewsOpen && (
          <ReviewsPanel
            expertId={expertData.expertId}
            expertName={expertData.expertName}
            scheme={scheme}
            onClose={() => setReviewsOpen(false)}
          />
        )}
        <DrawerItems>
          <DrawerItem>
            <DIIcon $scheme={scheme}><FaMapMarkerAlt size={13} /></DIIcon>
            <div><DILabel>Location</DILabel><DIValue>{expertData.expertInfo.location}</DIValue></div>
          </DrawerItem>
          <DrawerItem onClick={() => setHoursExpanded(!hoursExpanded)} style={{ cursor: 'pointer', flexWrap: 'wrap' }}>
            <DIIcon $scheme={scheme}><FaClock size={13} /></DIIcon>
            <div style={{ flex: 1, minWidth: 0 }}>
              <DILabel>Hours</DILabel>
              <DIValue style={expertData.workingHours?.isOpen ? { color: '#16a34a', fontWeight: 600 } : {}}>
                {expertData.workingHours?.isOpen ? 'Open' : 'Closed'} · {expertData.workingHours?.currentStatus?.replace(/^(Open|Closed) · /, '') || `Closes ${expertData.expertInfo.hourClose}`}
              </DIValue>
            </div>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{
              transform: hoursExpanded ? 'rotate(180deg)' : '', transition: 'transform 0.2s', color: '#94a3b8'
            }}>
              <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {expertData.workingHours?.schedule && (
              <MobileSchedule $expanded={hoursExpanded} style={{ width: '100%', paddingLeft: 42 }}>
                {expertData.workingHours.schedule.map((item, i) => (
                  <MobileScheduleRow key={i} $isToday={item.isToday}>
                    <span>{item.day}</span>
                    <span style={{ color: item.hours === 'Closed' ? '#ef4444' : undefined }}>{item.hours}</span>
                  </MobileScheduleRow>
                ))}
              </MobileSchedule>
            )}
          </DrawerItem>
          <DrawerItem>
            <DIIcon $scheme={scheme}><FaGlobe size={13} /></DIIcon>
            <div><DILabel>Website</DILabel><DIValue>{expertData.expertInfo.site}</DIValue></div>
          </DrawerItem>
          <DrawerItem>
            <DIIcon $scheme={scheme}><FaEnvelope size={13} /></DIIcon>
            <div><DILabel>Email</DILabel><DIValue>{expertData.expertInfo.email}</DIValue></div>
          </DrawerItem>
          <DrawerItem>
            <DIIcon $scheme={scheme}><FaPhone size={13} /></DIIcon>
            <div><DILabel>Phone</DILabel><DIValue>{expertData.expertInfo.phone}</DIValue></div>
          </DrawerItem>
        </DrawerItems>
      </Drawer>
    </MobileContainer>
  );
};

export default ChatPageMobile;
